import { useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useProjectStore } from '../../store/projectStore';
import type { SizingConfig } from '../../types';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';

const DEFAULT_SIZING_CONFIG: SizingConfig = {
  vcpuOvercommitRatio: 4,
  memoryOvercommitRatio: 1,
  platformOverheadPct: 0.15,
  haModel: 'n_plus_1',
  redundancyModel: 'active_passive',
  drTier: 'warm',
  drComputeFractionPct: 0.5,
  storageArchitecture: 'traditional_san',
  storageUsableToRawRatio: 1.33,
  storageGrowthBufferPct: 0.2,
  raidWritePenaltyFactor: 4,
  perHostTdpWatts: 500,
  storageChangeRatePct: 0.05,
  drRpoHours: 4,
};

const s = {
  form: {
    maxWidth: '520px',
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#8FA4B5',
    marginBottom: '6px',
  },
  required: {
    color: '#E05555',
    marginLeft: '2px',
  },
  input: {
    display: 'block',
    width: '100%',
    backgroundColor: '#0a1929',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '6px',
    padding: '9px 12px',
    fontSize: '14px',
    color: '#E8EDF2',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  inputError: {
    borderColor: '#E05555',
  },
  textarea: {
    display: 'block',
    width: '100%',
    backgroundColor: '#0a1929',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '6px',
    padding: '9px 12px',
    fontSize: '14px',
    color: '#E8EDF2',
    outline: 'none',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    minHeight: '80px',
    fontFamily: 'inherit',
  },
  errorMsg: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#E05555',
  },
  optionalHint: {
    fontSize: '12px',
    color: '#4C5C68',
    marginLeft: '4px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '8px',
  },
  btnPrimary: {
    padding: '9px 20px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '7px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

interface ProjectFormProps {
  onNext: () => void;
}

export function ProjectForm({ onNext }: ProjectFormProps) {
  const project = useProjectStore(st => st.project);
  const setProject = useProjectStore(st => st.setProject);

  const [name, setName] = useState(project?.name ?? '');
  const [clientName, setClientName] = useState(project?.clientName ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [errors, setErrors] = useState<{ name?: string; clientName?: string }>({});

  // Dirty if local state diverges from what is committed to the store.
  const isDirty =
    name !== (project?.name ?? '') ||
    clientName !== (project?.clientName ?? '') ||
    description !== (project?.description ?? '');

  // Block React Router navigation (e.g. TopBar "← Projects") when form is dirty.
  const blocker = useBlocker(isDirty);

  function validate() {
    const e: typeof errors = {};
    if (!name.trim()) e.name = 'Project name is required';
    if (!clientName.trim()) e.clientName = 'Client name is required';
    return e;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const baselineId = project?.scenarios[0]?.id ?? uuidv4();
    const now = new Date().toISOString();

    setProject({
      id: project?.id ?? uuidv4(),
      name: name.trim(),
      clientName: clientName.trim(),
      description: description.trim() || undefined,
      createdAt: project?.createdAt ?? now,
      updatedAt: now,
      applications: project?.applications ?? [],
      activeScenarioId: project?.activeScenarioId ?? baselineId,
      scenarios: project?.scenarios ?? [
        {
          id: baselineId,
          name: 'Baseline',
          sizingConfig: DEFAULT_SIZING_CONFIG,
          createdAt: now,
          isBaseline: true,
        },
      ],
    });

    onNext();
  }

  return (
    <>
      <form onSubmit={handleSubmit} style={s.form}>
        <div style={s.field}>
          <label style={s.label}>
            Project Name<span style={s.required}>*</span>
          </label>
          <input
            type="text"
            value={name}
            placeholder="e.g. City Hospital Infrastructure Sizing"
            onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
            style={{ ...s.input, ...(errors.name ? s.inputError : {}) }}
          />
          {errors.name && <p style={s.errorMsg}>{errors.name}</p>}
        </div>

        <div style={s.field}>
          <label style={s.label}>
            Client Name<span style={s.required}>*</span>
          </label>
          <input
            type="text"
            value={clientName}
            placeholder="e.g. City Hospital NHS Trust"
            onChange={e => { setClientName(e.target.value); setErrors(p => ({ ...p, clientName: undefined })); }}
            style={{ ...s.input, ...(errors.clientName ? s.inputError : {}) }}
          />
          {errors.clientName && <p style={s.errorMsg}>{errors.clientName}</p>}
        </div>

        <div style={s.field}>
          <label style={s.label}>
            Description<span style={s.optionalHint}>(optional)</span>
          </label>
          <textarea
            value={description}
            placeholder="Brief engagement overview or scope notes"
            onChange={e => setDescription(e.target.value)}
            style={s.textarea}
          />
        </div>

        <div style={s.footer}>
          <button type="submit" style={s.btnPrimary} data-primary-cta>
            Next: Applications →
          </button>
        </div>
      </form>

      {/* Guard navigation away (e.g. TopBar "← Projects") when form has unsaved edits */}
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onStay={() => blocker.reset?.()}
        onLeave={() => blocker.proceed?.()}
      />
    </>
  );
}
